import { ChangeDetectionStrategy, Component, OnInit, inject, computed, signal, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, timer } from 'rxjs';
import { ProjectsService } from '../../core/services/projects.service';
import { environment } from '../../../environments/environment';
import { DuplicateProjectDialogComponent } from '../../shared/components/duplicate-project-dialog/duplicate-project-dialog';
import { ApiError, DuplicateRequest, DuplicateRun, ProjectSummary } from '../../shared/models';
import { projectSlugPart } from '../../shared/utils/slug';

@Component({
	selector: 'app-projects',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [RouterLink, SlicePipe, FormsModule, DuplicateProjectDialogComponent],
	templateUrl: './projects.html',
	styleUrl: './projects.scss',
})
export class ProjectsComponent implements OnInit {
	protected service = inject(ProjectsService);
	protected backendUrl = environment.backendUrl;
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private destroyRef = inject(DestroyRef);
	private confirmationService = inject(ConfirmationService);
	private messages = inject(MessageService);

	readonly pageSize = 12;
	readonly currentPage = signal(1);
	readonly searchQuery = signal('');
	readonly viewMode = signal<'grid' | 'list'>('grid');

	/** Tracks project slugs whose thumbnail failed to load */
	readonly failedThumbnails = signal(new Set<string>());
	/** Tracks project slugs whose thumbnail has a mask (height > width) */
	readonly maskedThumbnails = signal(new Set<string>());

	readonly filteredProjects = computed(() => {
		const q = this.searchQuery().toLowerCase().trim();
		if (!q) return this.service.projects();
		return this.service.projects().filter(p =>
			p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
		);
	});

	readonly totalPages = computed(() =>
		Math.max(1, Math.ceil(this.filteredProjects().length / this.pageSize))
	);

	readonly paginatedProjects = computed(() => {
		const page = Math.min(this.currentPage(), this.totalPages());
		const start = (page - 1) * this.pageSize;
		return this.filteredProjects().slice(start, start + this.pageSize);
	});

	readonly rangeStart = computed(() =>
		this.filteredProjects().length === 0 ? 0 : (Math.min(this.currentPage(), this.totalPages()) - 1) * this.pageSize + 1
	);

	readonly rangeEnd = computed(() =>
		Math.min(Math.min(this.currentPage(), this.totalPages()) * this.pageSize, this.filteredProjects().length)
	);

	readonly pageNumbers = computed(() => {
		const total = this.totalPages();
		const current = Math.min(this.currentPage(), total);
		if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

		const pages: (number | null)[] = [1];
		if (current > 3) pages.push(null);
		for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
			pages.push(i);
		}
		if (current < total - 2) pages.push(null);
		if (total > 1) pages.push(total);
		return pages;
	});

	protected companySlug = '';
	protected campaignSlug = '';

	ngOnInit(): void {
		this.route.paramMap
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(params => {
				this.companySlug = params.get('companySlug') ?? '';
				this.campaignSlug = params.get('campaignSlug') ?? '';

				if (!this.companySlug || !this.campaignSlug) {
					console.warn('ProjectsComponent mounted without company/campaign slugs');
					return;
				}
				this.service.loadCampaignProjects(this.companySlug, this.campaignSlug);
			});

		this.route.queryParamMap
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(params => {
				const page = parseInt(params.get('page') ?? '1', 10);
				this.currentPage.set(page > 0 ? page : 1);
			});
	}

	onThumbnailError(slug: string): void {
		this.failedThumbnails.update(set => {
			const next = new Set(set);
			next.add(slug);
			return next;
		});
	}

	onThumbnailLoad(slug: string, event: Event): void {
		const img = event.target as HTMLImageElement;
		if (img.naturalHeight > img.naturalWidth) {
			this.maskedThumbnails.update(set => {
				const next = new Set(set);
				next.add(slug);
				return next;
			});
		}
	}

	goToPage(page: number): void {
		const clamped = Math.max(1, Math.min(page, this.totalPages()));
		// Pagination via matrix params on current route
		this.router.navigate([], { queryParams: { page: clamped }, queryParamsHandling: 'merge' });
	}

	// ── Duplication (DEV-1091) ───────────────────────────────────────────────

	protected readonly duplicateTarget = signal<ProjectSummary | null>(null);
	protected readonly duplicateVisible = signal(false);
	protected readonly duplicateStarting = signal(false);
	protected readonly duplicateStartError = signal<string | null>(null);
	protected readonly duplicateRun = signal<DuplicateRun | null>(null);
	/** Source project's coordinate space, fetched when the dialog opens so the format can be prefilled. */
	protected readonly duplicateSourceWidth = signal(1920);
	protected readonly duplicateSourceHeight = signal(1080);
	/** Slug of a copy currently being written, so the list can mark it. */
	protected readonly duplicatingSlug = signal<string | null>(null);
	private duplicatePollSub: Subscription | null = null;

	/**
	 * Project-part slugs already used in this campaign — what the dialog
	 * validates against, since that's the half the user types.
	 */
	protected readonly takenSlugs = computed(() =>
		this.service.projects().map(p => projectSlugPart(p.slug, this.companySlug, this.campaignSlug)),
	);

	protected openDuplicate(project: ProjectSummary, event?: Event): void {
		event?.stopPropagation();
		event?.preventDefault();
		this.duplicateTarget.set(project);
		this.duplicateRun.set(null);
		this.duplicateStartError.set(null);
		this.duplicateStarting.set(false);
		// Defaults for a project whose meta request fails — the dialog still
		// opens rather than blocking on a size it only needs for a prefill.
		this.duplicateSourceWidth.set(1920);
		this.duplicateSourceHeight.set(1080);
		this.service.getProjectMeta(project.slug).subscribe({
			next: (meta) => {
				this.duplicateSourceWidth.set(meta.origCoordsWidth);
				this.duplicateSourceHeight.set(meta.origCoordsHeight);
			},
			error: () => undefined,
		});
		this.duplicateVisible.set(true);
	}

	protected onDuplicateVisibleChange(visible: boolean): void {
		this.duplicateVisible.set(visible);
		// Closing the dialog does NOT stop the copy — the run keeps going
		// server-side and the list badge keeps showing it. Only the per-run
		// polling for the dialog's own progress bar stops with the dialog when
		// the run has already finished.
		if (!visible && this.duplicateRun()?.status !== 'running') {
			this.stopDuplicatePolling();
			this.duplicateRun.set(null);
		}
	}

	protected startDuplicate(body: DuplicateRequest): void {
		const source = this.duplicateTarget();
		if (!source) return;
		this.duplicateStarting.set(true);
		this.duplicateStartError.set(null);

		this.service.duplicateProject(source.slug, body).subscribe({
			next: (start) => {
				this.duplicateStarting.set(false);
				this.duplicatingSlug.set(start.targetSlug);
				// The copy's documents already exist, so it shows up in the list
				// immediately — refresh so the user sees it while media copy.
				this.reloadProjects();
				this.pollDuplicateRun(source.slug, start.runId);
			},
			error: (err: ApiError) => {
				this.duplicateStarting.set(false);
				this.duplicateStartError.set(err?.error?.error ?? 'Duplicate failed');
			},
		});
	}

	private pollDuplicateRun(sourceSlug: string, runId: string): void {
		this.stopDuplicatePolling();
		this.duplicatePollSub = timer(0, 1000)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.service.getDuplicateRun(sourceSlug, runId).subscribe({
					next: (run) => {
						this.duplicateRun.set(run);
						if (run.status === 'running') return;
						this.stopDuplicatePolling();
						this.duplicatingSlug.set(null);
						// Thumbnail and counts only become real once the media
						// are there, so the finished copy needs one more reload.
						this.reloadProjects();
						if (run.status === 'error') {
							this.messages.add({ severity: 'error', summary: 'Duplicate failed', detail: run.error ?? 'The copy did not finish.' });
						} else if (run.failures.length > 0) {
							this.messages.add({
								severity: 'warn',
								summary: 'Copy incomplete',
								detail: `"${run.targetName}" is missing ${run.failures.length} media file(s).`,
							});
						} else {
							this.messages.add({ severity: 'success', summary: 'Project duplicated', detail: run.targetName });
						}
					},
					error: () => {
						this.stopDuplicatePolling();
						this.duplicatingSlug.set(null);
					},
				});
			});
	}

	private stopDuplicatePolling(): void {
		this.duplicatePollSub?.unsubscribe();
		this.duplicatePollSub = null;
	}

	protected openDuplicatedProject(run: DuplicateRun): void {
		this.duplicateVisible.set(false);
		this.stopDuplicatePolling();
		this.duplicateRun.set(null);
		if (!this.companySlug || !this.campaignSlug) return;
		this.router.navigate(['/', this.companySlug, 'campaigns', this.campaignSlug, run.targetProjectId, 'editor']);
	}

	private reloadProjects(): void {
		if (!this.companySlug || !this.campaignSlug) return;
		this.service.loadCampaignProjects(this.companySlug, this.campaignSlug);
	}

	delete(slug: string): void {
		this.confirmationService.confirm({
			header: 'Delete project',
			message: `Delete project "${slug}"? This removes the project, all of its scenes, images, media files, and uploaded data. Cannot be undone.`,
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			acceptButtonProps: { severity: 'danger' },
			accept: () => {
				this.service.deleteProject(slug).subscribe(() => this.reloadProjects());
			},
		});
	}
}
